import { defineStore } from 'pinia'
import { getError } from '@/utils/errors'
import type {
  Salespoint,
  SalespointCreateData,
  SalespointUpdateData,
  SalespointResponse,
  SalespointCreateResponse,
  SalespointUpdateResponse,
  SalespointPaymentTerminal,
  SalespointPaymentTerminalResponse,
  SalespointPrinter,
  SalespointPrinterResponse,
  SalespointOption
} from '@/modules/settings/salespoints/salespoint.types'
import {
  getSalespoints,
  getSalespointById,
  createSalespoint as createSalespointApi,
  updateSalespoint as updateSalespointApi,
  deleteSalespoint as deleteSalespointApi,
  deactivateSalespoint as deactivateSalespointApi,
  activateSalespoint as activateSalespointApi,
  getSalespointsOptions as getSalespointsOptionsApi
} from '@/modules/settings/salespoints/services/salespoint.api'
import { isNetworkOrTimeoutError } from '@/services/api/base'

export const useSalespointStore = defineStore('salespoint', () => {
  /**
   * Fetch all salespoints
   * @param page - The page number to fetch
   * @param filters - Filters to apply to the salespoints
   * @param pageSize - Number of items per page
   * @returns Promise<PaginationResponse<Salespoint>> - The fetched salespoints with pagination info
   */
  const fetchSalespoints = async (
    page: number = 1,
    filters: ApiFilter[] = [],
    pageSize: number = 10
  ): Promise<PaginationResponse<Salespoint>> => {
    const response = await getSalespoints(page, filters, pageSize)

    if (!response.error) {
      return {
        currentPage: page,
        totalPages: response.total_pages,
        totalCount: response.total_count,
        data: response.data.map((salespointResponse: SalespointResponse) => ({
          id: salespointResponse.id,
          referenceXid: salespointResponse.reference_xid,
          name: salespointResponse.name,
          active: salespointResponse.active,
          inactiveAt: salespointResponse.inactive_at,
          metadata: salespointResponse.metadata,
          locationId: salespointResponse.location_id,
          printerId: salespointResponse.printer_id,
          paymentTerminalId: salespointResponse.payment_terminal_id,
          createdAt: salespointResponse.inserted_at,
          updatedAt: salespointResponse.updated_at,
          location: salespointResponse.location,
          printer: buildSalespointPrinter(salespointResponse.printer),
          paymentTerminal: buildSalespointPaymentTerminal(salespointResponse.payment_terminal)
        }))
      }
    } else {
      return { error: getError(response), data: [] }
    }
  }

  /**
   * Fetch a single salespoint by ID
   * @param id - The ID of the salespoint to fetch
   * @returns Promise<Salespoint | undefined> - The fetched salespoint or undefined
   */
  const fetchSalespointById = async (id: string): Promise<Salespoint | undefined> => {
    const response = await getSalespointById(id)

    if (response && !response.error) {
      return {
        id: response.id,
        referenceXid: response.reference_xid,
        name: response.name,
        active: response.active,
        inactiveAt: response.inactive_at,
        metadata: response.metadata,
        locationId: response.location_id,
        printerId: response.printer_id,
        paymentTerminalId: response.payment_terminal_id,
        createdAt: response.inserted_at,
        updatedAt: response.updated_at,
        location: response.location,
        printer: buildSalespointPrinter(response.printer),
        paymentTerminal: buildSalespointPaymentTerminal(response.payment_terminal)
      }
    } else if (response && isNetworkOrTimeoutError(response.errorCode)) {
      return { name: '', locationId: '' }
    } else {
      return undefined
    }
  }

  /**
   * Create a new salespoint
   * @param data - The salespoint data to create
   * @returns Promise<Salespoint | undefined> - The created salespoint or undefined on error
   */
  const createSalespoint = async (data: SalespointCreateData): Promise<SalespointCreateResponse> => {
    const response = await createSalespointApi(data)

    if (!response.error) {
      return response
    } else {
      return { success: false, error: getError(response) }
    }
  }

  /**
   * Update a salespoint
   * @param id - The salespoint ID to update
   * @param data - The salespoint data to update
   * @returns Promise<SalespointUpdateResponse> The updated salespoint
   */
  const updateSalespoint = async (id: string, data: SalespointUpdateData): Promise<SalespointUpdateResponse> => {
    const response = await updateSalespointApi(id, data)

    if (!response.error) {
      return response
    } else {
      return { success: false, error: getError(response) }
    }
  }

  /**
   * Delete a salespoint
   * @param id - The salespoint ID to delete
   * @returns Promise<ApiNoContentResponse> The deleted salespoint
   */
  const deleteSalespoint = async (id: string): Promise<ApiNoContentResponse> => {
    return await deleteSalespointApi(id)
  }

  /**
   * Deactivate a salespoint
   * @param id - The salespoint ID to deactivate
   * @returns Promise<ApiNoContentResponse> The deactivated salespoint
   */
  const deactivateSalespoint = async (id: string): Promise<ApiNoContentResponse> => {
    return await deactivateSalespointApi(id)
  }

  /**
   * Activate a salespoint
   * @param id - The salespoint ID to activate
   * @returns Promise<ApiNoContentResponse> The activated salespoint
   */
  const activateSalespoint = async (id: string): Promise<ApiNoContentResponse> => {
    return await activateSalespointApi(id)
  }

  async function getSalespointsOptions(): Promise<{ label: string; value: string; locationId: string }[]> {
    const salespoints = await getSalespointsOptionsApi()
    return salespoints.data.salespoints.map((salespoint: SalespointOption) => ({
      label: salespoint.name,
      value: salespoint.id,
      locationId: salespoint.locationId
    }))
  }

  function buildSalespointPaymentTerminal(
    paymentTerminal: SalespointPaymentTerminalResponse | undefined
  ): SalespointPaymentTerminal | undefined {
    if (!paymentTerminal) {
      return undefined
    }

    return {
      id: paymentTerminal.id,
      name: paymentTerminal.name,
      stripePaymentTerminalXid: paymentTerminal.stripe_payment_terminal_xid
    }
  }

  function buildSalespointPrinter(printer: SalespointPrinterResponse | undefined): SalespointPrinter | undefined {
    if (!printer) {
      return undefined
    }

    return {
      id: printer.id,
      name: printer.name,
      agentId: printer.agent_id
    }
  }

  return {
    fetchSalespoints,
    fetchSalespointById,
    createSalespoint,
    updateSalespoint,
    deleteSalespoint,
    deactivateSalespoint,
    activateSalespoint,
    getSalespointsOptions
  }
})
